/**
 * Sanitize errors for admin/tenant UI.
 * Never surface developer / infra jargon (API request failed, HTTP 500, stacks, …).
 * Duck-types ApiError (status/statusCode/code) — no ApiClient import (avoids cycles).
 */
import { pickLocalizedApiMessage } from 'pi-kiosk-shared';
import { isInternalApiSkipError } from '../http/isInternalApiSkipError.js';
import i18n from '../i18n/i18n.js';
import { isAdminChunkLoadFailure } from './isAdminChunkLoadFailure.js';
import {
  isUnsafeAdminApiMessage,
  publicMessageForHttpStatus,
  resolveAdminApiFailureMessage,
} from './adminPublicApiFailureMessage.js';

export {
  isUnsafeAdminApiMessage,
  looksLikeDeveloperErrorMessage,
  publicMessageForHttpStatus,
  resolveAdminApiFailureMessage,
} from './adminPublicApiFailureMessage.js';

export interface ToAdminFacingErrorMessageOptions {
  readonly fallback?: string;
}

interface ApiErrorLike {
  readonly message: string;
  readonly status: number;
  readonly code?: string;
}

function tAdmin(key: string): string {
  return i18n.t(key, { ns: 'admin' });
}

function asApiErrorLike(error: unknown): ApiErrorLike | null {
  if (typeof error !== 'object' || error === null) {
    return null;
  }
  const record = error as {
    message?: unknown;
    status?: unknown;
    statusCode?: unknown;
    code?: unknown;
  };
  let status: number | undefined;
  if (typeof record.status === 'number') {
    status = record.status;
  } else if (typeof record.statusCode === 'number') {
    status = record.statusCode;
  } else {
    status = undefined;
  }
  if (typeof status !== 'number') {
    return null;
  }
  let message = '';
  if (typeof record.message === 'string') {
    message = record.message;
  } else if (error instanceof Error) {
    message = error.message;
  }
  return {
    message,
    status,
    code: typeof record.code === 'string' ? record.code : undefined,
  };
}

function isInternalCachedFailureLeak(error: unknown): boolean {
  return isInternalApiSkipError(error);
}

function isNetworkStyleMessage(message: string): boolean {
  if (isAdminChunkLoadFailure(message)) {
    return false;
  }
  const lower = message.toLowerCase();
  return (
    lower.includes('failed to fetch') ||
    lower.includes('network error') ||
    lower.includes('load failed') ||
    lower.includes('econnrefused') ||
    lower.includes('enotfound') ||
    lower.includes('etimedout') ||
    lower.includes('networkerror')
  );
}

/**
 * Resolve admin/tenant-safe message for banners, toasts, and ScreenState.
 * Prefer known ApiError.code / status maps; never return developer jargon.
 */
export function toAdminFacingErrorMessage(
  error: unknown,
  options?: ToAdminFacingErrorMessageOptions,
): string {
  const fallback =
    options?.fallback !== undefined &&
    isUnsafeAdminApiMessage(options.fallback) === false
      ? options.fallback
      : tAdmin('shared.errors.generic');

  if (isInternalCachedFailureLeak(error)) {
    const denyCached = asApiErrorLike(error);
    if (denyCached?.code === 'API_DENY_CACHED') {
      // Distinct from shared.errorNextStep so ScreenState does not duplicate retry copy.
      return tAdmin('shared.errors.forbidden');
    }
    return tAdmin('shared.errorNextStep');
  }

  if (isAdminChunkLoadFailure(error)) {
    return tAdmin('app.chunkStaleDeploy.message');
  }

  const apiError = asApiErrorLike(error);
  if (apiError !== null) {
    return resolveAdminApiFailureMessage({
      status: apiError.status,
      code: apiError.code,
      rawMessage: apiError.message,
    });
  }

  if (error instanceof Error) {
    const localized = pickLocalizedApiMessage(
      error.message,
      i18n.resolvedLanguage ?? i18n.language,
    ).trim();
    if (isNetworkStyleMessage(localized)) {
      return tAdmin('shared.errors.network');
    }
    if (localized.length > 0 && isUnsafeAdminApiMessage(localized) === false) {
      return localized;
    }
    return fallback;
  }

  if (typeof error === 'string') {
    const localized = pickLocalizedApiMessage(
      error,
      i18n.resolvedLanguage ?? i18n.language,
    ).trim();
    if (localized.length > 0 && isUnsafeAdminApiMessage(localized) === false) {
      return localized;
    }
  }

  return fallback;
}

/** Sanitize a pre-resolved message string (e.g. ScreenState `message` prop). */
export function sanitizeAdminFacingMessage(
  message: string | undefined,
  options?: { readonly status?: number; readonly fallback?: string },
): string {
  const fallback =
    options?.fallback !== undefined &&
    isUnsafeAdminApiMessage(options.fallback) === false
      ? options.fallback
      : tAdmin('shared.errors.generic');

  if (message === undefined || message.trim().length === 0) {
    return options?.status !== undefined
      ? publicMessageForHttpStatus(options.status)
      : fallback;
  }

  if (isAdminChunkLoadFailure(message)) {
    return tAdmin('app.chunkStaleDeploy.message');
  }

  if (isUnsafeAdminApiMessage(message) === false) {
    return pickLocalizedApiMessage(
      message,
      i18n.resolvedLanguage ?? i18n.language,
    ).trim();
  }

  if (options?.status !== undefined) {
    return publicMessageForHttpStatus(options.status);
  }

  if (isNetworkStyleMessage(message)) {
    return tAdmin('shared.errors.network');
  }

  return fallback;
}
